import { Box, Divider, List, Paragraph, UnstyledButton } from '@zillow/constellation-10';
import getLatestMessageDateText from '../../utils/getLatestMessageDateText';
import { FULLSTORY_EXCLUDE_CLASS_NAME } from '../../constants';
import MessageContent from './MessageContent';
import UnreadIndicator from '../UnreadIndicator';
import {
  conversationItemActivateStyles,
  conversationItemStyles,
  listItemStyles,
  messageContentWrapperStyles,
} from './styles';

export type ConversationItemProps = {
  title: string;
  subtitle: string;
  latestMessage: string;
  latestMessageDateMs: string;
  isMessageOwner: boolean;
  isUnread: boolean;
  isPhoneLead: boolean;
  isArchived?: boolean;
  isSpamOrReported?: boolean;
  footer?: React.ReactNode;
  thumbnail?: React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
  dataEventId?: string;
  conversationId?: string;
};

export default function ConversationItem({
  title,
  subtitle,
  latestMessage,
  latestMessageDateMs,
  isMessageOwner,
  isUnread,
  isPhoneLead,
  isArchived = false,
  isSpamOrReported = false,
  footer,
  thumbnail,
  isSelected,
  onClick,
  dataEventId,
  conversationId,
}: ConversationItemProps) {
  return (
    <List.Item
      data-testid="conversation-item"
      data-eventid={dataEventId}
      data-conversation-id={conversationId}
      css={listItemStyles}
    >
      {/* Not a button, so the footer may render its own; the activation button's click bubbles here. */}
      <Box
        css={{
          ...conversationItemStyles,
          ...(isSelected && {
            backgroundColor: 'bg.action.soft.default',
            borderLeft: '4px solid',
            borderLeftColor: 'border.express.trust.hero',
            _hover: {
              backgroundColor: 'bg.action.soft.hover',
            },
          }),
        }}
        onClick={onClick}
      >
        {thumbnail && <Box css={{ marginRight: 'tighter', flexShrink: 0, position: 'relative' }}>{thumbnail}</Box>}

        <Box css={{ flex: 1, minWidth: 0, paddingLeft: thumbnail ? 'tight' : 0 }}>
          <Box asChild css={conversationItemActivateStyles}>
            <UnstyledButton aria-current={isSelected ? 'true' : undefined} aria-pressed={isSelected}>
              {/* Title and Date */}
              <Box
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Paragraph
                  textStyle={isUnread ? 'body-bold' : 'body'}
                  css={{
                    flex: 1,
                    marginRight: 'tight',
                    truncate: 'end',
                  }}
                  className={FULLSTORY_EXCLUDE_CLASS_NAME}
                  data-testid="participant-name"
                >
                  {title}
                </Paragraph>

                <Paragraph textStyle="body-xs" css={{ color: 'text.subtle', flexShrink: 0 }}>
                  {getLatestMessageDateText(latestMessageDateMs)}
                </Paragraph>
              </Box>

              {/* Subtitle */}
              {subtitle && (
                <Paragraph
                  textStyle="body-xs"
                  css={{
                    color: 'text.subtle',
                    truncate: 'end',
                  }}
                  className={FULLSTORY_EXCLUDE_CLASS_NAME}
                >
                  {subtitle}
                </Paragraph>
              )}

              {/* Message & Unread indicator */}
              <Box data-testid="message-preview" css={messageContentWrapperStyles}>
                <MessageContent
                  isPhoneLead={isPhoneLead}
                  isMessageOwner={isMessageOwner}
                  latestMessage={latestMessage}
                  isArchived={isArchived}
                  isSpamOrReported={isSpamOrReported}
                  isUnread={isUnread}
                />

                {isUnread && <UnreadIndicator dataTestId="unread-badge" />}
              </Box>
            </UnstyledButton>
          </Box>

          {/* Footer components */}
          {footer && (
            <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'tight' }}>
              {footer}
            </Box>
          )}
        </Box>
      </Box>

      {/* Divider is rendered outside the row for better semantics */}
      <Divider />
    </List.Item>
  );
}
